import { Box, Typography, useTheme, Divider, useMediaQuery } from '@mui/material';
import { People, Visibility, PersonOutline, TrendingUp } from '@mui/icons-material';
import React from 'react';
import { AnalyticsSummary } from '../../../../../services/analytics/AnalyticsService';
import PortalCard from '../../../../../components/PortalCard';

interface VisitorStatsCardProps {
    summary: AnalyticsSummary | null;
}

const VisitorStatsCard: React.FC<VisitorStatsCardProps> = ({ summary }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const iconSize = isMobile ? 20 : 24;

    const stats = [
        {
            label: 'Page Views',
            value: summary?.totalPageViews || 0,
            icon: <Visibility sx={{ fontSize: iconSize, color: theme.palette.primary.main }} />,
        },
        {
            label: 'Product Views',
            value: summary?.totalProductViews || 0,
            icon: <TrendingUp sx={{ fontSize: iconSize, color: theme.palette.warning.main }} />,
        },
        {
            label: 'Anonymous',
            value: summary?.anonymousUsers || 0,
            icon: <PersonOutline sx={{ fontSize: iconSize, color: theme.palette.text.secondary }} />,
        },
        {
            label: 'Authenticated',
            value: summary?.authenticatedUsers || 0,
            icon: <People sx={{ fontSize: iconSize, color: theme.palette.success.main }} />,
        },
    ];

    return (
        <PortalCard
            sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Fixed Header */}
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
                <Typography
                    variant='h6'
                    fontWeight='bold'
                    fontFamily='primaryFont'
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                    Visitor Insights
                </Typography>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    fontFamily='primaryFont'
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                    Total Visits: {summary?.totalVisits || 0}
                </Typography>
            </Box>

            {/* Scrollable Content */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 3 },
                    '&::-webkit-scrollbar': {
                        width: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: 3,
                    }
                }}
            >
                {stats.map((stat, index) => (
                    <Box key={index}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: { xs: 1, sm: 1.5 },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                                {stat.icon}
                                <Typography
                                    variant='body2'
                                    fontFamily='primaryFont'
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    {stat.label}
                                </Typography>
                            </Box>
                            <Typography
                                variant='h6'
                                fontWeight='bold'
                                fontFamily='primaryFont'
                                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                            >
                                {stat.value.toLocaleString()}
                            </Typography>
                        </Box>
                        {index < stats.length - 1 && <Divider />}
                    </Box>
                ))}
            </Box>
        </PortalCard>
    );
};

export default VisitorStatsCard;

